// src/components/FirmaPad.js
// ═══════════════════════════════════════════════════════════════════════════
// ACQUISIZIONE DELLA FIRMA — un solo componente per tutta l'app (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Usato da rapportini (tecnico e cliente), manutenzione, sopralluoghi, giornale di cantiere.
// Prima c'erano 4 copie diverse, tutte con lo stesso difetto: il riquadro era disegnato a
// larghezza fissa ma mostrato rimpicciolito, e il tratto usciva spostato rispetto al dito.
// Qui:
//  • la posizione si calcola sulla dimensione reale del riquadro (anche se si ruota il
//    telefono o cambia la finestra): il tratto esce sempre sotto il dito;
//  • sul telefono la firma si apre A TUTTO SCHERMO (foglio bianco con riga guida);
//    sul computer resta nella finestra, in un riquadro ampio;
//  • tratto morbido (curve tra i punti), nitido sugli schermi ad alta densità, spessore che
//    varia un poco con la velocità come una penna;
//  • dito, mouse e penna con gli stessi eventi (Pointer Events); la pagina non scorre;
//  • «Annulla tratto», «Cancella», e «Salva» attivo solo se c'è una firma vera;
//  • la firma salvata è ritagliata ai bordi, su sfondo trasparente, e leggera;
//  • TEMA SCURO DEL TELEFONO: alcuni browser (Chrome/Android con «tema scuro per i siti»)
//    scuriscono da soli le pagine chiare: il foglio bianco diventava nero e la firma scura
//    spariva. Il foglio bianco ora è dipinto DENTRO il riquadro di disegno (il browser non
//    lo tocca) e le firme salvate si mostrano con FirmaImmagine, sempre su bianco. Il tratto
//    resta scuro: su PDF e stampe (sempre su carta bianca) si vede come prima.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { vibra } from "../utils/preferenzeDispositivo";

const COLORE = "#111827";
const MIN_LUNGHEZZA = 60;   // lunghezza totale minima del tratto (px) per una firma "vera"
const MIN_AMPIEZZA = 30;    // ampiezza minima (px) del disegno
const LARGHEZZA_SALVATA = 600;

const eTelefono = () => typeof window !== "undefined" &&
  (window.matchMedia?.("(pointer: coarse)").matches && Math.min(window.innerWidth, window.innerHeight) < 820);

// Spessore in base alla velocità: lento = più spesso, veloce = più sottile
const spessore = (v, base) => Math.max(base * 0.55, Math.min(base * 1.35, base * (1.35 - v * 0.35)));

function disegnaTratto(ctx, punti, base) {
  if (punti.length === 0) return;
  ctx.strokeStyle = COLORE; ctx.fillStyle = COLORE; ctx.lineCap = "round"; ctx.lineJoin = "round";
  if (punti.length === 1) {
    ctx.beginPath(); ctx.arc(punti[0].x, punti[0].y, base * 0.6, 0, Math.PI * 2); ctx.fill(); return;
  }
  for (let i = 1; i < punti.length; i++) {
    const p0 = punti[i - 2] || punti[i - 1], p1 = punti[i - 1], p2 = punti[i];
    const m1 = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 }, m2 = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    ctx.lineWidth = p2.w || base;
    ctx.beginPath(); ctx.moveTo(i === 1 ? p1.x : m1.x, i === 1 ? p1.y : m1.y);
    ctx.quadraticCurveTo(p1.x, p1.y, m2.x, m2.y); ctx.stroke();
  }
  const u = punti[punti.length - 1], pu = punti[punti.length - 2];
  ctx.lineWidth = u.w || base; ctx.beginPath(); ctx.moveTo((pu.x + u.x) / 2, (pu.y + u.y) / 2); ctx.lineTo(u.x, u.y); ctx.stroke();
}

// I tratti si salvano in pixel di un riquadro di RIFERIMENTO (quello in cui è iniziata la
// firma). Se il riquadro cambia forma (rotazione del telefono, finestra ridimensionata) la
// firma si ridisegna con la STESSA scala in orizzontale e in verticale, centrata: non si
// deforma mai, e i tratti nuovi si aggiungono nello stesso riferimento.
function trasformazione(dim, rif) {
  if (!rif || !rif.w || !rif.h || !dim.w || !dim.h) return { s: 1, ox: 0, oy: 0 };
  const s = Math.min(dim.w / rif.w, dim.h / rif.h);
  return { s, ox: (dim.w - rif.w * s) / 2, oy: (dim.h - rif.h * s) / 2 };
}

/** La superficie di firma: riempie il contenitore. */
function Superficie({ tratti, setTratti, rif, setRif, base, guida }) {
  const wrapRef = useRef(null), canvasRef = useRef(null);
  const corrente = useRef(null);
  const puntatore = useRef(null); // un solo dito/penna alla volta: il palmo appoggiato non disegna
  const [dim, setDim] = useState({ w: 0, h: 0 });

  // dimensione reale del riquadro (e a ogni ridimensionamento/rotazione)
  useLayoutEffect(() => {
    const el = wrapRef.current; if (!el) return;
    const misura = () => setDim({ w: el.clientWidth, h: el.clientHeight });
    misura();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(misura) : null;
    ro?.observe(el); window.addEventListener("resize", misura);
    return () => { ro?.disconnect(); window.removeEventListener("resize", misura); };
  }, []);

  const T = trasformazione(dim, rif || dim);
  const aSchermo = (t) => t.map(p => ({ x: T.ox + p.x * T.s, y: T.oy + p.y * T.s, w: (p.w || base) * T.s }));

  const ridisegna = useCallback(() => {
    const c = canvasRef.current; if (!c || !dim.w) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(dim.w * dpr); c.height = Math.round(dim.h * dpr);
    const ctx = c.getContext("2d"); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, dim.w, dim.h);
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, dim.w, dim.h); // foglio bianco anche col tema scuro del telefono
    const tutti = corrente.current ? [...tratti, corrente.current] : tratti;
    tutti.forEach(t => disegnaTratto(ctx, aSchermo(t), base));
  }, [dim, tratti, base, rif]);
  useEffect(() => { ridisegna(); }, [ridisegna]);

  // posizione nel riferimento della firma
  const pos = (e, Tx) => {
    const r = canvasRef.current.getBoundingClientRect();
    const sx = (e.clientX - r.left) * (dim.w / (r.width || dim.w || 1)), sy = (e.clientY - r.top) * (dim.h / (r.height || dim.h || 1));
    return { x: (sx - Tx.ox) / Tx.s, y: (sy - Tx.oy) / Tx.s, t: e.timeStamp };
  };
  const giu = (e) => {
    if (e.button !== undefined && e.button > 0) return;
    if (puntatore.current !== null && puntatore.current !== e.pointerId) return;
    e.preventDefault();
    puntatore.current = e.pointerId;
    canvasRef.current.setPointerCapture?.(e.pointerId);
    let Tx = T;
    if (!rif) { setRif({ w: dim.w, h: dim.h }); Tx = { s: 1, ox: 0, oy: 0 }; } // il primo tratto fissa il riferimento
    const p = pos(e, Tx); corrente.current = [{ ...p, w: base }];
    corrente.T = Tx;
    ridisegna();
  };
  const muovi = (e) => {
    if (!corrente.current || e.pointerId !== puntatore.current) return;
    e.preventDefault();
    const Tx = corrente.T || T;
    const nativo = e.nativeEvent || e;
    const eventi = typeof nativo.getCoalescedEvents === "function" ? nativo.getCoalescedEvents() : [];
    (eventi.length ? eventi : [nativo]).forEach(ev => {
      const p = pos(ev, Tx), ult = corrente.current[corrente.current.length - 1];
      const dist = Math.hypot(p.x - ult.x, p.y - ult.y) * Tx.s;
      if (dist < 0.8) return;                                   // ignora micro-tremolii
      const v = dist / Math.max(1, p.t - ult.t);                // px/ms
      const w = (ult.w || base) * 0.6 + spessore(v, base) * 0.4; // variazione graduale
      corrente.current.push({ ...p, w });
    });
    // disegno incrementale dell'ultimo pezzo (veloce anche su telefoni lenti)
    const ctx = canvasRef.current.getContext("2d");
    disegnaTratto(ctx, corrente.current.slice(-4).map(q => ({ x: Tx.ox + q.x * Tx.s, y: Tx.oy + q.y * Tx.s, w: q.w * Tx.s })), base);
  };
  const su = (e) => {
    if (e.pointerId !== puntatore.current) return;
    puntatore.current = null;
    if (!corrente.current) return;
    e.preventDefault();
    const t = corrente.current.map(({ x, y, w }) => ({ x, y, w })); corrente.current = null; corrente.T = null;
    setTratti(prev => [...prev, t]);
  };

  return (
    <div ref={wrapRef} style={{ position: "absolute", inset: 0, touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}>
      <canvas ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", touchAction: "none", cursor: "crosshair" }}
        onPointerDown={giu} onPointerMove={muovi} onPointerUp={su} onPointerCancel={su}
        onContextMenu={e => e.preventDefault()} />
      {/* riga guida sopra il foglio (il foglio bianco è dipinto nel riquadro di disegno) */}
      {guida && <>
        <div style={{ position: "absolute", left: "8%", right: "8%", bottom: "26%", borderBottom: "2px solid #d1d5db", pointerEvents: "none" }} />
        <div style={{ position: "absolute", left: "8%", bottom: "27%", fontSize: 22, color: "#9ca3af", pointerEvents: "none" }}>✕</div>
        {tratti.length === 0 && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#d1d5db", fontSize: 15, pointerEvents: "none" }}>✍️ Firma qui</div>}
      </>}
    </div>
  );
}

/** Firma valida: tratto abbastanza lungo e disegno abbastanza ampio (in px del riferimento). */
export function firmaValida(tratti) {
  let lung = 0, minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  tratti.forEach(t => t.forEach((p, i) => {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
    if (i) lung += Math.hypot(p.x - t[i - 1].x, p.y - t[i - 1].y);
  }));
  return lung >= MIN_LUNGHEZZA && Math.max(maxX - minX, maxY - minY) >= MIN_AMPIEZZA;
}

/** Immagine PNG della firma, ritagliata ai bordi con un piccolo margine, larga al massimo 600 px. */
function esporta(tratti, base) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  tratti.forEach(t => t.forEach(p => { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }));
  const m = base * 3 + 6;
  minX -= m; minY -= m; maxX += m; maxY += m;
  const bw = Math.max(1, maxX - minX), bh = Math.max(1, maxY - minY);
  const scala = Math.min(2, LARGHEZZA_SALVATA / bw);
  const c = document.createElement("canvas");
  c.width = Math.round(bw * scala); c.height = Math.round(bh * scala);
  const ctx = c.getContext("2d"); ctx.setTransform(scala, 0, 0, scala, -minX * scala, -minY * scala);
  tratti.forEach(t => disegnaTratto(ctx, t, base));
  return c.toDataURL("image/png");
}

/**
 * Mostra una firma salvata (PNG trasparente) sempre su foglio bianco, anche quando il
 * telefono scurisce la pagina da solo: la firma è disegnata in un riquadro di disegno con
 * il suo fondo bianco, che il browser non ricolora. Stesse dimensioni di un'immagine (style
 * con maxWidth/maxHeight). Se l'immagine non si carica, si torna a <img>.
 */
export function FirmaImmagine({ src, alt = "Firma", style }) {
  const ref = useRef(null);
  const [errore, setErrore] = useState(false);
  useEffect(() => {
    if (!src) return undefined;
    let vivo = true;
    const im = new Image();
    im.onload = () => {
      const c = ref.current; if (!vivo || !c) return;
      c.width = im.naturalWidth || 1; c.height = im.naturalHeight || 1;
      const x = c.getContext("2d");
      x.fillStyle = "#fff"; x.fillRect(0, 0, c.width, c.height); x.drawImage(im, 0, 0);
    };
    im.onerror = () => { if (vivo) setErrore(true); };
    im.src = src;
    return () => { vivo = false; };
  }, [src]);
  if (!src) return null;
  if (errore) return <img src={src} alt={alt} style={style} />;
  return <canvas ref={ref} role="img" aria-label={alt} style={{ ...style, width: "auto", height: "auto" }} />;
}

const btn = (tipo) => ({
  padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: tipo === "ok" ? 700 : 600, cursor: "pointer", fontFamily: "'Inter',sans-serif",
  border: tipo === "ok" ? "1px solid #059669" : "1px solid #e5e7eb",
  background: tipo === "ok" ? "#059669" : "#fff", color: tipo === "ok" ? "#fff" : "#374151",
});

/**
 * Riquadro di firma. onSave(dataUrl PNG) — onCancel() facoltativo.
 * schermoIntero: true/false per forzare; se non indicato, a tutto schermo sui telefoni.
 */
export default function FirmaPad({ label = "Firma", onSave, onCancel, onBozza, schermoIntero }) {
  const intero = schermoIntero ?? eTelefono();
  const [tratti, setTratti] = useState([]);
  const [rif, setRif] = useState(null); // riquadro di riferimento, fissato dal primo tratto
  const areaRef = useRef(null);
  // firma cancellata (o tutti i tratti annullati): il prossimo tratto fissa un nuovo riferimento
  useEffect(() => { if (tratti.length === 0) setRif(null); }, [tratti.length]);
  // a tutto schermo: niente scorrimento della pagina sotto
  useEffect(() => {
    if (!intero) return;
    const prima = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prima; };
  }, [intero]);

  const base = intero ? 3 : 2.6;
  const valida = firmaValida(tratti);
  const salva = () => { if (valida) { vibra("ok"); onSave(esporta(tratti, base)); } };
  // Bozza: dopo ogni tratto chi usa il riquadro riceve la firma (o null se non è ancora
  // valida), così una firma fatta ma non confermata con «Salva firma» non va persa.
  const ultimaBozza = useRef(undefined);
  useEffect(() => {
    if (!onBozza) return;
    const d = firmaValida(tratti) ? esporta(tratti, base) : null;
    if (d !== ultimaBozza.current) { ultimaBozza.current = d; onBozza(d); }
  }, [tratti]);

  const comandi = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <button type="button" onClick={() => setTratti(t => t.slice(0, -1))} disabled={!tratti.length} style={{ ...btn(), opacity: tratti.length ? 1 : .5 }}>↶ Annulla tratto</button>
      <button type="button" onClick={() => setTratti([])} disabled={!tratti.length} style={{ ...btn(), opacity: tratti.length ? 1 : .5 }}>Cancella</button>
      <span style={{ flex: 1 }} />
      {onCancel && <button type="button" onClick={onCancel} style={btn()}>Chiudi</button>}
      <button type="button" onClick={salva} disabled={!valida} title={valida ? "" : "Firma ancora troppo corta"} style={{ ...btn("ok"), opacity: valida ? 1 : .45, cursor: valida ? "pointer" : "not-allowed" }}>Salva firma</button>
    </div>
  );

  if (intero) {
    return createPortal(
      <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "#fff", display: "flex", flexDirection: "column", padding: "max(10px, env(safe-area-inset-top)) 12px max(10px, env(safe-area-inset-bottom))" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 6 }}>{label}
          <span style={{ fontWeight: 400, fontSize: 12, color: "#6b7280" }}> · puoi girare il telefono in orizzontale</span></div>
        <div ref={areaRef} style={{ position: "relative", flex: 1, border: "2px dashed #d1d5db", borderRadius: 10, background: "#fff", overflow: "hidden" }}>
          <Superficie tratti={tratti} setTratti={setTratti} rif={rif} setRif={setRif} base={base} guida />
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <button type="button" onClick={() => setTratti(t => t.slice(0, -1))} disabled={!tratti.length} style={{ ...btn(), flex: 1, opacity: tratti.length ? 1 : .5 }}>↶ Annulla tratto</button>
          <button type="button" onClick={() => setTratti([])} disabled={!tratti.length} style={{ ...btn(), flex: 1, opacity: tratti.length ? 1 : .5 }}>Cancella</button>
          {onCancel && <button type="button" onClick={onCancel} style={{ ...btn(), flex: 1 }}>Chiudi</button>}
        </div>
        <button type="button" onClick={salva} disabled={!valida} style={{ ...btn("ok"), marginTop: 8, padding: "13px", fontSize: 15, width: "100%", opacity: valida ? 1 : .45 }}>
          {valida ? "Salva firma" : "Firma nel riquadro"}</button>
      </div>, document.body);
  }
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#374151" }}>{label}</div>
      <div ref={areaRef} style={{ position: "relative", width: "100%", height: 220, border: "2px dashed #d1d5db", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
        <Superficie tratti={tratti} setTratti={setTratti} rif={rif} setRif={setRif} base={base} guida />
      </div>
      <div style={{ marginTop: 10 }}>{comandi}</div>
    </div>
  );
}
